import type {
  Instrument,
  InstrumentCalibration,
  InstrumentRepository,
  TechnicalAssetDocument,
  InstrumentStatus
} from "./equipment-domain.js";
import { PermissionCode } from "../domain/identity/access-types.js";
import type { RequestContext } from "../application/context/request-context.js";
import type { AuthorizationService } from "../permissions/authorization.js";
import type { AuditRecorder } from "../audit/audit-recorder.js";
import { AuditAction } from "../audit/audit-log.js";
import { AppError, ErrorCode } from "../shared/errors/app-error.js";
import type { InstrumentId, InstrumentCalibrationId, ClientId, EquipmentId, AreaId } from "../shared/kernel/identifiers.js";
import {
  assertTechnicalAssetHierarchy,
  belongsToProject,
  requireAssetClientId,
  type TechnicalAssetHierarchyLookup
} from "./technical-asset-hierarchy.js";

export interface InstrumentListFilters {
  equipmentId?: string;
  areaId?: string;
  status?: string;
  projectId?: string;
}

export class InstrumentService {
  constructor(
    private readonly repo: InstrumentRepository,
    private readonly auth: AuthorizationService,
    private readonly audit?: AuditRecorder
  ) {}

  private hierarchy: TechnicalAssetHierarchyLookup | null = null;
  private authorizedClientIdsResolver: ((context: RequestContext) => Promise<readonly ClientId[]>) | null = null;

  /** Projeto, Área e Equipamento conferidos contra o Cliente do Instrumento. */
  public attachHierarchyLookup(lookup: TechnicalAssetHierarchyLookup): void {
    this.hierarchy = lookup;
  }

  /**
   * "Todos os Clientes" (contexto sem Cliente) é a união dos Clientes
   * AUTORIZADOS à sessão — nunca uma consulta sem filtro de tenant.
   */
  public attachAuthorizedClientIdsResolver(resolver: (context: RequestContext) => Promise<readonly ClientId[]>): void {
    this.authorizedClientIdsResolver = resolver;
  }

  private async scopeClientIds(context: RequestContext): Promise<ClientId[]> {
    if (context.activeClientId) return [context.activeClientId];
    if (!this.authorizedClientIdsResolver) return [];
    return [...new Set((await this.authorizedClientIdsResolver(context)).map(id => String(id)))] as ClientId[];
  }

  /** Projeto de cada equipamento vinculado — para filtrar cadastros antigos, sem `projectId` próprio. */
  private async equipmentProjects(instruments: readonly Instrument[]): Promise<Map<string, string | null>> {
    const projects = new Map<string, string | null>();
    if (!this.hierarchy) return projects;
    for (const inst of instruments) {
      if (!inst.equipmentId || projects.has(inst.equipmentId)) continue;
      const equipment = await this.hierarchy.getEquipment(inst.clientId, inst.equipmentId);
      projects.set(inst.equipmentId, equipment?.projectId ?? null);
    }
    return projects;
  }

  public async getInstrument(context: RequestContext, id: string): Promise<Instrument | null> {
    this.auth.require(context, PermissionCode.InstrumentRead);
    const inst = await this.repo.getInstrument(context.activeClientId, id as InstrumentId);
    if (!inst) return null;

    // IDOR Tenant Isolation Protection
    if (inst.clientId !== context.activeClientId) {
      throw new AppError(ErrorCode.Authorization, "Cross-tenant access denied", {
        safeMessage: "Access denied to instrument from another client"
      });
    }

    if (this.audit) {
      await this.audit.record(context, {
        action: AuditAction.Create,
        entityType: "Instrument",
        entityId: inst.id,
        previousState: null,
        newState: null,
        metadata: { action: "INSTRUMENT_VIEWED", tag: inst.tag }
      }).catch(() => {});
    }

    return inst;
  }

  public async listInstruments(context: RequestContext, filters?: InstrumentListFilters): Promise<Instrument[]> {
    this.auth.require(context, PermissionCode.InstrumentRead);
    const { projectId, ...repoFilters } = filters ?? {};
    const clientIds = await this.scopeClientIds(context);
    const list = (await Promise.all(clientIds.map(clientId => this.repo.listInstruments(clientId, repoFilters)))).flat();
    if (!projectId) return list;
    const equipmentProjects = await this.equipmentProjects(list);
    return list.filter(inst => belongsToProject(inst, projectId, id => equipmentProjects.get(id) ?? null));
  }

  public async createInstrument(context: RequestContext, data: Partial<Instrument>): Promise<Instrument> {
    this.auth.require(context, PermissionCode.InstrumentCreate);

    // O Cliente do Instrumento é o do contexto da chamada (o servidor já
    // conferiu o acesso da sessão a ele). Um `clientId` diferente no corpo é
    // tentativa de gravar em outro tenant.
    const clientId = requireAssetClientId(context.activeClientId);
    if (data.clientId && String(data.clientId) !== clientId) {
      throw new AppError(ErrorCode.Authorization, "Tenant isolation violation", {
        safeMessage: "O Cliente do cadastro não corresponde ao Cliente selecionado."
      });
    }
    await assertTechnicalAssetHierarchy(this.hierarchy, {
      clientId,
      projectId: data.projectId ?? null,
      areaId: data.areaId ?? null,
      equipmentId: data.equipmentId ?? null
    });

    const tag = (data.tag || "").trim().toUpperCase();
    if (!tag) {
      throw new AppError(ErrorCode.Validation, "TAG do instrumento é obrigatório", {
        safeMessage: "TAG é obrigatório."
      });
    }

    // Check unique tag for this client
    const existingList = await this.repo.listInstruments(clientId);
    const duplicate = existingList.find(i => i.tag.trim().toUpperCase() === tag);
    if (duplicate) {
      throw new AppError(ErrorCode.Conflict, `TAG '${tag}' já está cadastrado para este cliente`, {
        safeMessage: `TAG '${tag}' já existe neste cliente.`
      });
    }

    const now = new Date().toISOString();
    const id = (data.id || `INST-${Date.now().toString(36)}-${Math.random().toString(36).substring(2, 6)}`.toUpperCase()) as InstrumentId;

    const instrument: Instrument = {
      id,
      clientId,
      tag,
      name: data.name || tag,
      instrumentType: data.instrumentType || 'PRESSURE_GAUGE',
      description: data.description || null,
      manufacturer: data.manufacturer || null,
      model: data.model || null,
      serialNumber: data.serialNumber || null,
      rangeMin: data.rangeMin ?? null,
      rangeMax: data.rangeMax ?? null,
      unit: data.unit || 'bar',
      accuracy: data.accuracy || null,
      processConnection: data.processConnection || null,
      signalType: data.signalType || null,
      projectId: data.projectId || null,
      equipmentId: (data.equipmentId as EquipmentId) || null,
      pipingSystemId: data.pipingSystemId || null,
      pipingLineId: data.pipingLineId || null,
      areaId: (data.areaId as AreaId) || null,
      system: data.system || null,
      subsystem: data.subsystem || null,
      service: data.service || null,
      location: data.location || null,
      status: (data.status as InstrumentStatus) || 'ACTIVE',
      notes: data.notes || null,
      calibrationRequired: data.calibrationRequired ?? true,
      lastCalibrationDate: data.lastCalibrationDate || null,
      nextCalibrationDate: data.nextCalibrationDate || null,
      certificateNumber: data.certificateNumber || null,
      certificateDocumentId: data.certificateDocumentId || null,
      calibrationStatus: data.calibrationStatus || (data.calibrationRequired ? 'VALID' : 'NOT_REQUIRED'),
      version: 1,
      createdAt: now,
      createdBy: context.userId || 'system',
      updatedAt: now,
      updatedBy: context.userId || 'system'
    };

    await this.repo.saveInstrument(instrument);

    if (this.audit) {
      await this.audit.record(context, {
        action: AuditAction.Create,
        entityType: "Instrument",
        entityId: instrument.id,
        previousState: null,
        newState: { tag: instrument.tag, type: instrument.instrumentType, status: instrument.status },
        metadata: { action: "INSTRUMENT_CREATED" }
      }).catch(() => {});
    }

    return instrument;
  }

  public async updateInstrument(context: RequestContext, data: Partial<Instrument> & { id: string }): Promise<Instrument> {
    this.auth.require(context, PermissionCode.InstrumentUpdate);

    const existing = await this.repo.getInstrument(context.activeClientId, data.id as InstrumentId);
    if (!existing) {
      throw new AppError(ErrorCode.NotFound, "Instrumento não encontrado", {
        safeMessage: "Instrumento não encontrado."
      });
    }

    const tag = (data.tag || existing.tag).trim().toUpperCase();
    if (tag !== existing.tag.trim().toUpperCase()) {
      const existingList = await this.repo.listInstruments(context.activeClientId);
      const duplicate = existingList.find(i => i.tag.trim().toUpperCase() === tag && i.id !== existing.id);
      if (duplicate) {
        throw new AppError(ErrorCode.Conflict, `TAG '${tag}' já está cadastrado para este cliente`, {
          safeMessage: `TAG '${tag}' já existe neste cliente.`
        });
      }
    }

    const now = new Date().toISOString();
    const updated: Instrument = {
      ...existing,
      ...data,
      id: existing.id,
      clientId: existing.clientId,
      tag,
      version: (existing.version || 1) + 1,
      updatedAt: now,
      updatedBy: context.userId || 'system'
    };

    // O Cliente não muda na edição; o vínculo ALTERADO (Projeto, Área,
    // Equipamento) continua tendo de pertencer a ele. Vínculo intocado de um
    // cadastro antigo não é reavaliado — editar a faixa de medição não pode
    // travar por uma Área legada.
    const changed = (a: string | null | undefined, b: string | null | undefined) => (a ?? null) !== (b ?? null);
    const projectChanged = changed(updated.projectId, existing.projectId);
    const equipmentChanged = changed(updated.equipmentId, existing.equipmentId);
    await assertTechnicalAssetHierarchy(this.hierarchy, {
      clientId: existing.clientId,
      projectId: projectChanged || equipmentChanged ? updated.projectId ?? null : null,
      areaId: changed(updated.areaId, existing.areaId) ? updated.areaId : null,
      equipmentId: projectChanged || equipmentChanged ? updated.equipmentId : null
    });

    await this.repo.saveInstrument(updated);

    if (this.audit) {
      await this.audit.record(context, {
        action: AuditAction.Update,
        entityType: "Instrument",
        entityId: updated.id,
        previousState: { tag: existing.tag, status: existing.status, version: existing.version },
        newState: { tag: updated.tag, status: updated.status, version: updated.version },
        metadata: { action: "INSTRUMENT_UPDATED" }
      }).catch(() => {});
    }

    return updated;
  }

  /**
   * Controlled Deletion of Instrument:
   * Checks if calibrations, documents or inspections exist.
   * If history exists -> REJECTS Hard Delete and instructs Inactivation.
   */
  public async deleteInstrument(
    context: RequestContext,
    instrumentId: string,
    reason: string
  ): Promise<{ deleted: boolean; message: string }> {
    this.auth.require(context, PermissionCode.InstrumentDelete);

    if (!reason || reason.trim().length < 3) {
      throw new AppError(ErrorCode.Validation, "Justificativa obrigatória", {
        safeMessage: "Informe o motivo/justificativa para a exclusão do instrumento."
      });
    }

    const inst = await this.repo.getInstrument(context.activeClientId, instrumentId as InstrumentId);
    if (!inst) {
      throw new AppError(ErrorCode.NotFound, "Instrumento não encontrado", {
        safeMessage: "Instrumento não encontrado."
      });
    }

    const calibrations = await this.repo.listCalibrations(context.activeClientId, inst.id);
    const docs = await this.repo.listDocuments(context.activeClientId, inst.id);

    if (calibrations.length > 0 || docs.length > 0) {
      if (this.audit) {
        await this.audit.record(context, {
          action: AuditAction.Delete,
          entityType: "Instrument",
          entityId: inst.id,
          previousState: { tag: inst.tag, status: inst.status },
          newState: null,
          metadata: {
            action: "INSTRUMENT_DELETE_DENIED_HISTORY_EXISTS",
            calibrationsCount: calibrations.length,
            documentsCount: docs.length,
            reason
          }
        }).catch(() => {});
      }

      throw new AppError(
        ErrorCode.Conflict,
        "ESTE INSTRUMENTO POSSUI HISTÓRICO DE CALIBRAÇÃO/DOCUMENTAL E NÃO PODE SER EXCLUÍDO DEFINITIVAMENTE.",
        {
          safeMessage: "ESTE INSTRUMENTO POSSUI HISTÓRICO DE CALIBRAÇÃO OU DOCUMENTOS E NÃO PODE SER EXCLUÍDO DEFINITIVAMENTE. Utilize a ação de inativação."
        }
      );
    }

    await this.repo.deleteInstrument(context.activeClientId, inst.id);

    if (this.audit) {
      await this.audit.record(context, {
        action: AuditAction.Delete,
        entityType: "Instrument",
        entityId: inst.id,
        previousState: { tag: inst.tag, status: inst.status },
        newState: null,
        metadata: {
          action: "INSTRUMENT_DELETED",
          deletedBy: context.userId,
          deletionReason: reason
        }
      }).catch(() => {});
    }

    return {
      deleted: true,
      message: `Instrumento ${inst.tag} excluído definitivamente com sucesso.`
    };
  }

  public async inactivateInstrument(
    context: RequestContext,
    instrumentId: string,
    reason: string
  ): Promise<Instrument> {
    this.auth.require(context, PermissionCode.InstrumentInactivate);

    if (!reason || reason.trim().length < 3) {
      throw new AppError(ErrorCode.Validation, "Justificativa obrigatória", {
        safeMessage: "Informe o motivo/justificativa para a inativação."
      });
    }

    const inst = await this.repo.getInstrument(context.activeClientId, instrumentId as InstrumentId);
    if (!inst) {
      throw new AppError(ErrorCode.NotFound, "Instrumento não encontrado", {
        safeMessage: "Instrumento não encontrado."
      });
    }

    const now = new Date().toISOString();
    const updated: Instrument = {
      ...inst,
      status: 'INACTIVE',
      inactivatedAt: now,
      inactivatedBy: context.userId,
      inactivationReason: reason,
      version: (inst.version || 1) + 1,
      updatedAt: now,
      updatedBy: context.userId
    };

    await this.repo.saveInstrument(updated);

    if (this.audit) {
      await this.audit.record(context, {
        action: AuditAction.Inactivate,
        entityType: "Instrument",
        entityId: inst.id,
        previousState: { status: inst.status },
        newState: { status: 'INACTIVE' },
        metadata: {
          action: "INSTRUMENT_INACTIVATED",
          inactivatedBy: context.userId,
          inactivationReason: reason
        }
      }).catch(() => {});
    }

    return updated;
  }

  public async reactivateInstrument(
    context: RequestContext,
    instrumentId: string,
    reason: string = "Reativação de cadastro de instrumento"
  ): Promise<Instrument> {
    this.auth.require(context, PermissionCode.InstrumentReactivate);

    const inst = await this.repo.getInstrument(context.activeClientId, instrumentId as InstrumentId);
    if (!inst) {
      throw new AppError(ErrorCode.NotFound, "Instrumento não encontrado", {
        safeMessage: "Instrumento não encontrado."
      });
    }

    const now = new Date().toISOString();
    const updated: Instrument = {
      ...inst,
      status: 'ACTIVE',
      inactivatedAt: null,
      inactivatedBy: null,
      inactivationReason: null,
      version: (inst.version || 1) + 1,
      updatedAt: now,
      updatedBy: context.userId
    };

    await this.repo.saveInstrument(updated);

    if (this.audit) {
      await this.audit.record(context, {
        action: AuditAction.StatusChange,
        entityType: "Instrument",
        entityId: inst.id,
        previousState: { status: inst.status },
        newState: { status: 'ACTIVE' },
        metadata: {
          action: "INSTRUMENT_REACTIVATED",
          reactivatedBy: context.userId,
          reactivationReason: reason
        }
      }).catch(() => {});
    }

    return updated;
  }

  // ==========================================
  // CALIBRATIONS
  // ==========================================

  public async listCalibrations(context: RequestContext, instrumentId: string): Promise<InstrumentCalibration[]> {
    this.auth.require(context, PermissionCode.InstrumentRead);
    return this.repo.listCalibrations(context.activeClientId, instrumentId as InstrumentId);
  }

  public async addCalibration(
    context: RequestContext,
    data: {
      instrumentId: string;
      date: string;
      certificate: string;
      result: 'APPROVED' | 'APPROVED_WITH_RESTRICTION' | 'REJECTED';
      responsible: string;
      calibrationEntity?: string;
      nextDueDate: string | null;
      documentId?: string;
      errorMaxFound?: string;
      uncertainty?: string;
      notes?: string;
    }
  ): Promise<InstrumentCalibration> {
    this.auth.require(context, PermissionCode.InstrumentUpdate);

    const inst = await this.repo.getInstrument(context.activeClientId, data.instrumentId as InstrumentId);
    if (!inst) {
      throw new AppError(ErrorCode.NotFound, "Instrumento não encontrado", {
        safeMessage: "Instrumento não encontrado para registro de calibração."
      });
    }

    // Validation: nextDueDate >= date
    if (data.nextDueDate && new Date(data.nextDueDate) < new Date(data.date)) {
      throw new AppError(ErrorCode.Validation, "Data da próxima calibração não pode ser anterior à data da calibração atual", {
        safeMessage: "Data de vencimento deve ser maior ou igual à data de calibração."
      });
    }

    const now = new Date().toISOString();
    const id = `CAL-${Date.now().toString(36)}-${Math.random().toString(36).substring(2, 6)}`.toUpperCase() as InstrumentCalibrationId;

    const cal: InstrumentCalibration = {
      id,
      clientId: context.activeClientId,
      instrumentId: inst.id,
      date: data.date,
      certificate: data.certificate,
      result: data.result,
      responsible: data.responsible,
      calibrationEntity: data.calibrationEntity || null,
      nextDueDate: data.nextDueDate,
      documentId: data.documentId || null,
      errorMaxFound: data.errorMaxFound || null,
      uncertainty: data.uncertainty || null,
      notes: data.notes || null,
      createdAt: now,
      createdBy: context.userId
    };

    await this.repo.saveCalibration(cal);

    // Update instrument summary status
    const isExpired = data.nextDueDate ? new Date(data.nextDueDate) < new Date() : false;
    const updatedInst: Instrument = {
      ...inst,
      lastCalibrationDate: data.date,
      nextCalibrationDate: data.nextDueDate,
      certificateNumber: data.certificate,
      certificateDocumentId: data.documentId || inst.certificateDocumentId,
      calibrationStatus: isExpired ? 'EXPIRED' : (data.result === 'REJECTED' ? 'DUE' : 'VALID'),
      updatedAt: now,
      updatedBy: context.userId
    };
    await this.repo.saveInstrument(updatedInst);

    if (this.audit) {
      await this.audit.record(context, {
        action: AuditAction.Create,
        entityType: "InstrumentCalibration",
        entityId: cal.id,
        previousState: null,
        newState: { instrumentTag: inst.tag, certificate: cal.certificate, result: cal.result },
        metadata: { action: "INSTRUMENT_CALIBRATION_ADDED", instrumentId: inst.id }
      }).catch(() => {});
    }

    return cal;
  }

  public async deleteCalibration(context: RequestContext, calibrationId: string): Promise<void> {
    this.auth.require(context, PermissionCode.InstrumentUpdate);
    await this.repo.deleteCalibration(context.activeClientId, calibrationId as InstrumentCalibrationId);
  }

  // ==========================================
  // DOCUMENTS
  // ==========================================

  public async listDocuments(context: RequestContext, instrumentId: string): Promise<TechnicalAssetDocument[]> {
    this.auth.require(context, PermissionCode.InstrumentRead);
    return this.repo.listDocuments(context.activeClientId, instrumentId);
  }

  public async linkDocument(context: RequestContext, doc: TechnicalAssetDocument): Promise<TechnicalAssetDocument> {
    this.auth.require(context, PermissionCode.TechnicalAssetDocumentsManage);
    await this.repo.saveDocument(doc);
    return doc;
  }

  public async unlinkDocument(context: RequestContext, documentId: string): Promise<void> {
    this.auth.require(context, PermissionCode.TechnicalAssetDocumentsManage);
    await this.repo.removeDocument(context.activeClientId, documentId);
  }
}
